import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Volume2, VolumeX } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Omar',
    rating: 5,
    videoSrc: '/videos/Ratones1.mov', // Reemplaza con tu video
    initials: 'O',
    gradient: 'from-pink-400 to-rose-500',
  },
  {
    id: 2,
    name: 'Equipo All Star Mexico',
    rating: 5,
    videoSrc: '/videos/Ratones2.mp4',
    initials: 'EASM',
    gradient: 'from-blue-400 to-indigo-500',
  },
  {
    id: 3,
    name: 'Alex',
    rating: 5,
    videoSrc: '/videos/Ratones3.mp4',
    initials: 'A',
    gradient: 'from-purple-400 to-violet-500',
  },
  {
    id: 4,
    name: 'Familia Valadez',
    rating: 5,
    videoSrc: '/videos/Ratones4.mp4',
    initials: 'FV',
    gradient: 'from-orange-400 to-amber-500',
  },
  {
    id: 5,
    name: 'Familia Casillas',
    rating: 5,
    videoSrc: '/videos/Ratones5.mp4',
    initials: 'FC',
    gradient: 'from-orange-400 to-amber-500',
  },
  {
    id: 6,
    name: 'Familia Salcedo',
    rating: 5,
    videoSrc: '/videos/Ratones6.mp4',
    initials: 'FS',
    gradient: 'from-orange-400 to-amber-500',
  },
];

// Poster genérico con gradiente + iniciales — sin imágenes externas
const VideoPoster = ({ initials, gradient, name }) => (
  <div className={`absolute inset-0 bg-gradient-to-br ${gradient} flex flex-col items-center justify-center gap-3`}>
    <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/40 flex items-center justify-center shadow-lg">
      <span className="text-white text-2xl font-bold">{initials}</span>
    </div>
    <span className="text-white/80 text-sm font-medium">{name}</span>
    <div className="flex items-center gap-1 mt-1">
      <div className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

const VideoTestimonial = ({ testimonial, index }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  return (
    <motion.div
      className="bg-white rounded-3xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 overflow-hidden group w-full sm:w-72"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -6 }}
    >
      {/* Video */}
      <div className="relative w-full aspect-[4/3] bg-gray-900">

        {/* Poster genérico — se oculta cuando el video arranca */}
        <div className={`absolute inset-0 transition-opacity duration-700 ${started ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <VideoPoster
            initials={testimonial.initials}
            gradient={testimonial.gradient}
            name={testimonial.name}
          />
        </div>

        <video
          ref={videoRef}
          src={testimonial.videoSrc}
          autoPlay
          muted
          playsInline
          loop
          className="w-full h-full object-cover"
          onPlay={() => setStarted(true)}
        />

        {/* Botón mute siempre visible */}
        <button
          onClick={toggleMute}
          className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors duration-200"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        {/* Indicador de sonido desactivado */}
        {muted && started && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/40 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
            <VolumeX className="w-3 h-3" />
            <span>Sin sonido</span>
          </div>
        )}
      </div>

      {/* Nombre y estrellas */}
      <div className="px-5 py-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center flex-shrink-0`}>
          <span className="text-white text-xs font-bold">{testimonial.initials}</span>
        </div>
        <div>
          <h4 className="font-bold text-gray-800 text-sm leading-tight">{testimonial.name}</h4>
          <div className="flex items-center gap-0.5 mt-0.5">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-current" />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Testimonials = () => {
  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-purple-50 to-pink-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
            Lo que Dicen Nuestros Clientes
          </h2>
          <p className="mt-3 text-gray-500 text-lg max-w-md mx-auto">
            Experiencias reales de familias que viajaron con nosotros.
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-6">
          {testimonials.map((testimonial, index) => (
            <VideoTestimonial key={testimonial.id} testimonial={testimonial} index={index} />
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <a
            href="#quote"
            className="inline-block bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            Cotiza tu viaje ahora
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;